import Box from '@mui/material/Box';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import AlertMessages from 'common/Alert/AlertMessages';
import { useTranslation } from 'react-i18next';
import { checkPrivileges } from 'Helpers/CheckPrivileges';
import BackendPaginationTable from 'common/Table/TableWithPagination';
import { useNavigate, useParams } from 'react-router-dom';
import ComponentHeaderTwo from 'common/ComponentHeaderTwo/ComponentHeaderTwo';
import { ReactComponent as ClientGroupIcon } from "assets/SideBarIcons/customer-group.svg";
import { useGetClientGroupClientService, useAssignClientService, useUnassignClientService, useAssignAllClientService, useUnassignAllClientService } from 'subModule/src/services/clientgroup/useClientGroup';
import ClientGroupTabs from './ClientGroupTabs';
import ClientGroupClientSearchHeader from 'components/ClientGroup/ClientGroupClientSearchHeader';
import { SelectChangeEvent } from '@mui/material';
import { ClientGroupProfileService } from 'subModule/src/core/services';
import type { BreadcrumbItem } from 'Helpers/generalBreadcrumbs';

interface SearchFormData {
    [key: string]: string | any;
}
export default function ClientGroupClientTable() {
    const params = useParams();
    const groupId = params?.id;
    const navigate = useNavigate();
    const [pageNo, setPageNo] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(20);
    const [loading, setLoading] = useState<boolean>(false);
    const [filter, setFilter] = useState<string[]>(['name']);
    const [search, setSearch] = useState<any>({});
    const [searchFormData, setSearchFormData] = useState<SearchFormData>({ name: '', userName: '' });
    const [groupTitle, setGroupTitle] = useState('');
    const { data: tableData, isFetching, refetch } = useGetClientGroupClientService({ pageNumber: pageNo, size: pageSize, ...search }, +(params as any).id);
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();
    let allClientsAssigned: unknown = tableData?.pages[0].data?.content.map(item => item.linked).every(linked => linked === true);

    useEffect(() => {
        if (!groupId) return;
        let active = true;
        ClientGroupProfileService.getClientGroupByID(+groupId)
            .then((res: any) => {
                if (!active) return;
                const name = String(
                    res?.data?.title ||
                        res?.data?.secondaryTitle ||
                        res?.title ||
                        '',
                ).trim();
                if (name) setGroupTitle(name);
            })
            .catch(() => {});
        return () => {
            active = false;
        };
    }, [groupId]);

    const breadcrumbItems: BreadcrumbItem[] = useMemo(
        () => [
            { labelKey: 'general' },
            { labelKey: 'clientsGroup', href: '/client-groups' },
            {
                labelKey: 'title',
                ...(groupTitle ? { label: groupTitle } : {}),
                href: groupId ? `/client-group/${groupId}` : undefined,
            },
            { labelKey: 'assignClient' },
        ],
        [groupId, groupTitle],
    );

    const fetchData = useCallback(() => {
        refetch().then((res: any) => {
            if (!res?.data?.pages[0]?.success) enqueueSnackbar(<AlertMessages text={res?.data?.pages[0]?.message || "Something went wrong"} />, { variant: "error" });
        });
    }, []);
    useEffect(() => {
        fetchData();
    }, [pageNo, pageSize, search]);
    const handleChangePage = (pageNo: number) => {
        setPageNo(pageNo + 1);
    }
    const handleChangeRows = (pageSize: number) => {
        setPageSize(pageSize);
        setPageNo(1);
    }
    const clearSearch = () => {
        setFilter(['name']);
        setSearchFormData({ name: '', userName: '' });
        if (Object.keys(search).length) {
            setSearch({});
        }
    }
    const handleChangeFilter = (event: SelectChangeEvent<typeof filter>) => {
        const { target: { value }, } = event;
        setFilter(typeof value === 'string' ? value.split(',') : value,);
        setSearchFormData(prevFormData =>
            Object.fromEntries(Object.keys(prevFormData).map(key =>
                [key, value.includes(key) ? prevFormData[key] : '']
            ))
        );
        if (!value.length && Object.keys(search).length) {
            clearSearch();
        }
    };
    const handleChangeSearch = (field: string, value: any) => {
        const val = value;
        setSearchFormData((prevData) => ({
            ...prevData,
            [field]: val,
        }));
    };
    const handleSubmitSearch = () => {
        const filteredSearch: Record<string, string> = {};
        Object.entries(searchFormData).forEach(([key, value]) => {
            if (value) {
                filteredSearch[key] = value?.trim();
            }
        });
        if (Object.keys(filteredSearch).length > 0 && Object.values(filteredSearch).some(value => value)) {
            setSearch(filteredSearch);
            setPageNo(1);
            setPageSize(pageSize);
        }
    };
    const assignUnassignSingleEntity = (isChecked: boolean, row: any) => {
        setLoading(true);
        if (isChecked) {
            onAssignClient({ clientGroupId: +(params as any).id, clientId: row.id });
        } else {
            onUnassignClient({ clientGroupId: +(params as any).id, clientId: row.id });
        }
    }
    const assignUnassignAllEntities = (isChecked: boolean) => {
        setLoading(true);
        if (isChecked) {
            onAssignAllClients({ clientGroupId: +(params as any).id });
        } else {
            onUnassignAllClients({ clientGroupId: +(params as any).id });
        }
    }
    const { onAssignClient } = useAssignClientService({
        onSuccess: (msg) => {
            const response: any = {};
            Object.keys(msg).forEach((key, index) => { response[key] = Object.values(msg)[index]; });
            if (response.success) {
                if (tableData?.pages[0].data.content.length === tableData?.pages[0].data.content.filter(client => client.linked).length) {
                    allClientsAssigned = true;
                }
                refetch();
                setLoading(false);
                enqueueSnackbar(<AlertMessages statusCode={response.status} text={t('Client') + ' ' + t('assigned') + ' ' + t('successfully')} />, { variant: 'success' });
            } else {
                refetch();
                setLoading(false);
                enqueueSnackbar(<AlertMessages statusCode={response.status} />, { variant: 'error' });
            }
        },
        onError: (err: any) => {
            enqueueSnackbar(<AlertMessages statusCode={0} />, { variant: 'error' });
            if (err instanceof Error) {
                console.error(err.stack);
            }
        }
    })
    const { onUnassignClient } = useUnassignClientService({
        onSuccess: (data) => {
            const response: any = {};
            Object.keys(data).forEach((key, index) => { response[key] = Object.values(data)[index]; });
            if (response.success) {
                if (tableData?.pages[0].data.content.length !== tableData?.pages[0].data.content.filter(client => client.linked).length) {
                    allClientsAssigned = false;
                }
                refetch();
                setLoading(false);
                enqueueSnackbar(<AlertMessages statusCode={response.status} text={t('Client') + ' ' + t('unassigned') + ' ' + t('successfully')} />, { variant: 'success' });
            } else {
                refetch();
                setLoading(false);
                enqueueSnackbar(<AlertMessages statusCode={response.status} />, { variant: 'error' });
            }
        },
        onError: (err: any) => {
            console.log("err====,  ", err);
            enqueueSnackbar(<AlertMessages statusCode={0} />, { variant: 'error' });
        },
    });
    const { onAssignAllClients } = useAssignAllClientService({
        onSuccess: (msg) => {
            const response: any = {};
            Object.keys(msg).forEach((key, index) => { response[key] = Object.values(msg)[index]; });
            if (response.success) {
                allClientsAssigned = true;
                refetch();
                setLoading(false);
                enqueueSnackbar(<AlertMessages statusCode={response.status} text={t('All') + ' ' + t('Clients') + ' ' + t('assigned') + ' ' + t('successfully')} />, { variant: 'success' });
            } else {
                refetch();
                setLoading(false);
                enqueueSnackbar(<AlertMessages statusCode={response.status} />, { variant: 'error' });
            }
        },
        onError: (err: any) => {
            enqueueSnackbar(<AlertMessages statusCode={0} />, { variant: 'error' });
            if (err instanceof Error) {
                console.error(err.stack);
            }
        }
    })
    const { onUnassignAllClients } = useUnassignAllClientService({
        onSuccess: (data) => {
            const response: any = {};
            Object.keys(data).forEach((key, index) => { response[key] = Object.values(data)[index]; });
            if (response.success) {
                allClientsAssigned = false;
                refetch();
                setLoading(false);
                enqueueSnackbar(<AlertMessages statusCode={response.status} text={t('All') + ' ' + t('Clients') + ' ' + t('unassigned') + ' ' + t('successfully')} />, { variant: 'success' });
            } else {
                refetch();
                setLoading(false);
                enqueueSnackbar(<AlertMessages statusCode={response.status} />, { variant: 'error' });
            }
        },
        onError: (err: any) => {
            console.log("err====,  ", err);
            enqueueSnackbar(<AlertMessages statusCode={0} />, { variant: 'error' });
        },
    });

    return (
        <Box>
            <ComponentHeaderTwo
                showBtn={true}
                showSaveBtn={false}
                onClick={() => navigate('/client-groups')}
                heading={t('clientGroup')}
                icon={<ClientGroupIcon height={28} width={28} />}
                btnText={"back"}
                btnType={"back"}
                showCreateBtn={false}
                breadcrumbItems={breadcrumbItems}
            />
            <Box>
                <div className='flex'>
                    {params?.id ? <ClientGroupTabs params={params} selectedIndex={1} /> : null}
                    <BackendPaginationTable
                        columns={[
                            { id: 'name', label: 'name', numeric: false },
                            { id: 'userNameClientGroup', label: 'email', numeric: false },
                        ]}
                        data={tableData?.pages[0]?.data?.content || []}
                        pageNo={pageNo}
                        pageSize={pageSize}
                        tableData={tableData?.pages[0]?.data}
                        showCheckbox={true}
                        showActions={true}
                        enableSorting={true}
                        showHeader={true}
                        showSearch={true}
                        showFilter={true}
                        showCreateButton={false}
                        actions={[]}
                        handleChangePage={handleChangePage}
                        handleChangeRows={handleChangeRows}
                        assignUnassignSingleEntity={assignUnassignSingleEntity}
                        assignUnassignAllEntities={assignUnassignAllEntities}
                        allEntitiesAssigned={allClientsAssigned}
                        loadingSearch={isFetching || loading}
                        assignPrivilege={checkPrivileges('ROLE_CLIENTGROUP_CLIENT_ASSIGN')}
                        unassignPrivilege={checkPrivileges('ROLE_CLIENTGROUP_CLIENT_UNASSIGN')}
                        headerComponent={<ClientGroupClientSearchHeader 
                            searchFormData={searchFormData}
                            handleChangeFilter={handleChangeFilter}
                            handleChangeSearch={handleChangeSearch}
                            handleSubmitSearch={handleSubmitSearch}
                            clearSearch={clearSearch}
                            search={search}
                            {...{filter}}
                        />}
                    />
                </div>
            </Box>
        </Box>
    )
}